import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createBlock as _createBlock, createTextVNode as _createTextVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "main" }
const _hoisted_2 = { class: "notice" }
const _hoisted_3 = { class: "desc" }
const _hoisted_4 = {
  key: 0,
  class: "warning"
}
const _hoisted_5 = { class: "header" }
const _hoisted_6 = { class: "header__inputs" }
const _hoisted_7 = {
  key: 2,
  class: "header--arrow"
}
const _hoisted_8 = {
  key: 0,
  class: "empty-state"
}
const _hoisted_9 = { class: "empty-state__title" }
const _hoisted_10 = { class: "empty-state__description" }
const _hoisted_11 = {
  key: 1,
  class: "table-container"
}

import { computed, onMounted, ref } from 'vue';
import EditPageLayout from './EditPageLayout.vue';
import { i18n } from '@/i18n';
import EzTable from '@/corePagesModule/components/ezTable/EzTable.vue';
import EzTableRow from '@/corePagesModule/components/ezTable/EzTableRow.vue';
import EzHeader from '@/corePagesModule/components/ezTable/EzHeader.vue';
import EzCell from '@/corePagesModule/components/ezTable/EzCell.vue';
import { MODE_CONFIG, TargetMasterItems } from '../constants';
import { useBulkEditBusinessStore } from './useEditBusinessStore/index';
import { storeToRefs } from 'pinia';
import { useTableHeader } from '@/corePagesModule/composables/useTableHeader';
import { useColumnResizer } from '@/corePagesModule/composables/useColumnResizer';
import { HeaderList } from '@/coreDomainsModule/table';
import { EditBusinessViewModel } from './editBusinessViewModel';
import SingleSelectInput from '@/corePagesModule/SelectInput/SingleSelectInput.vue';
import { EditMasterItem, initMasterItem } from './useEditBusinessStore/types';
import TextInput from '@/unmanagedBusinessModule/components/TextInput.vue';
import { MasterPairResolverService } from '@/corePagesModule/service/masterPairResolverService';
import TableHeaderModifyButton from '@/corePagesModule/components/TableHeaderModifyButton.vue';
import TextButton from '@/components/input/Btn/TextButton.vue';
import { showInfoModal } from '@/corePagesModule/components/InfoModal/InfoModalManager';
import { FilterContentItem } from '@/corePagesModule/filterModal/filterModal';
import { diContainer } from '@/coreContainer/container';
import { waitLoading } from '@/corePagesModule/components/InfoModal/waitLoading';
import { toast } from '@/corePagesModule/components/Toast/ToastManager';
import { useColumnWidth } from '@/corePagesModule/composables/useColumnWidth';


export default /*@__PURE__*/_defineComponent({
  __name: 'EditMasterPage',
  setup(__props) {

const t = i18n.t;
const vm = new EditBusinessViewModel();
const store = useBulkEditBusinessStore();
const { state } = storeToRefs(store);
const { $reset, fetchEditBusiness } = store;

const tableRef = ref<HTMLElement | null>(null);
const tableWidthInfo = ref<Record<string, number | undefined>>({});
const selectedColumn = ref<string>('');
const targetMaster = ref<EditMasterItem>(initMasterItem());
const editedMaster = ref<EditMasterItem>(initMasterItem());
const isManualPairSelectionRequired = ref(false);
const pairMissingNotified = ref(false);
const targetSelectionRequestId = ref(0);

const pageTitle = computed(() => `${t('選択肢編集')}`);
const modeConfig = computed(() => MODE_CONFIG[state.value.businessMode]);
const tableType = computed(() => modeConfig.value.tableType.master);

const businessFreeColumnHeaderOptions = computed(() => {
  const headers = new Map<number, string>();
  for (const business of state.value.dataList) {
    for (const column of business.businessFreeColumnValues ?? []) {
      if (column.isConfigured) headers.set(column.columnNo, column.headerName);
    }
  }
  return [...headers.entries()]
    .sort(([a], [b]) => a - b)
    .map(([columnNo, headerName]) => ({ key: `businessFreeColumnValues-${columnNo}`, label: headerName, type: 'str' as const }));
});
const filteredFullHeaderOptions = computed(() => [
  ...modeConfig.value.headerOptions.filter((h) => h.key !== 'checkbox'),
  ...businessFreeColumnHeaderOptions.value,
]);
const fullHeaderKeys = computed(() => filteredFullHeaderOptions.value.map((h) => h.key));

const { headerList, updateDisplayHeader } = useTableHeader(fullHeaderKeys, tableType, []);
useColumnWidth({ tableType: tableType, widthInfo: tableWidthInfo });
const { onMousedownResizer } = useColumnResizer({
  tableType: tableType,
  widthInfo: tableWidthInfo,
  tableEl: tableRef,
});

const headerOptions = computed(() => filteredFullHeaderOptions.value.filter((h) => headerList.value.includes(h.key)));
const headerWidths = computed(() => headerOptions.value.map((h) => `${vm.getHeaderWidth(h, tableWidthInfo.value)}px`));
const ModifiableHeaderKeys = computed(
  (): HeaderList =>
    modeConfig.value.headerOptions
      .filter((h) => !['headerModify'].includes(h.key))
      .map((h) => ({
        key: h.key,
        text: h.label,
        isShown: headerList.value.includes(h.key),
      }))
);

const targetMasterFilterType = computed(
  (): EditMasterItem => ({
    code: `${toUpperSnakeCase(selectedColumn.value)}_CODE`,
    name: `${toUpperSnakeCase(selectedColumn.value)}${selectedColumn.value === 'warehouse' ? '' : '_NAME'}`,
  })
);

const canShowEditInputs = computed(
  () => targetMaster.value.code !== '' && targetMaster.value.name !== '' && state.value.loadingStatus.type === 'success'
);
const isEditedMasterXor = computed(() => {
  const isCodeChanged = editedMaster.value.code !== targetMaster.value.code;
  const isNameChanged = editedMaster.value.name !== targetMaster.value.name;
  return isCodeChanged !== isNameChanged;
});
const isBlank = computed(() => editedMaster.value.code === '' || editedMaster.value.name === '');
const isCommitDisable = computed(() => !canShowEditInputs.value || !isEditedMasterXor.value || isBlank.value);

/**
 * camelCaseをSelectInput用にUPPER_SNAKE_CASEに変換する
 * @param value - camelCaseの値
 */
const toUpperSnakeCase = (value: string): string => value.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toUpperCase();

/**
 * マスタのペア取得に失敗した際にモーダルを表示してフラグを立てる
 */
const showPairMissingModal = async () => {
  if (pairMissingNotified.value) return;
  pairMissingNotified.value = true;

  await showInfoModal({
    modalTitle: t('対応する選択肢が取得できません'),
    modalDescription: t('コードと名称をそれぞれ選択してください。選択した組み合わせに一致する取引を表示します。'),
    icon: 'error',
    colorStyle: 'error',
    backButtonLabel: t('OK'),
    isDisabledNextButton: true,
  });
};

/**
 * ターゲット選択中に古い一覧や変更後入力が一瞬表示されないようにする
 */
const clearFetchedEditBusiness = () => {
  state.value.filterItems = [];
  state.value.dataList = [];
  state.value.checkedIds = new Set();
  state.value.loadingStatus.type = 'loading';
  editedMaster.value = initMasterItem();
};

/**
 * 手動ペア選択モードを解除する
 */
const resetManualPairSelection = () => {
  isManualPairSelectionRequired.value = false;
  pairMissingNotified.value = false;
};

/**
 * 編集対象カラムの選択を解除する
 */
const clearColumnSelection = () => {
  targetSelectionRequestId.value++;
  selectedColumn.value = '';
  targetMaster.value = initMasterItem();
  editedMaster.value = initMasterItem();
  resetManualPairSelection();
  clearFetchedEditBusiness();
};

/**
 * 編集対象カラムをセットする
 * @param value - 値
 */
const setColumn = (value: string) => {
  if (value === '') {
    clearColumnSelection();
    return;
  }

  for (const item of TargetMasterItems) {
    if (value === t(`editMaster:${item}`) && selectedColumn.value !== item) {
      targetSelectionRequestId.value++;
      selectedColumn.value = item;
      targetMaster.value = initMasterItem();
      editedMaster.value = initMasterItem();
      resetManualPairSelection();
      clearFetchedEditBusiness();
      state.value.businessMode = ['customer', 'salesDestination'].includes(item) ? 'baseBusiness' : 'aggregateBusiness';
      return;
    }
  }
};

/**
 * ターゲット情報をセットする
 * @param type - タイプ
 * @param value - 値
 */
const setTargetMasterValue = async (type: 'code' | 'name', value: string) => {
  const requestId = ++targetSelectionRequestId.value;
  targetMaster.value[type] = value;
  clearFetchedEditBusiness();

  if (value === '') {
    targetMaster.value = initMasterItem();
    resetManualPairSelection();
    return;
  }

  const anotherType = type === 'code' ? 'name' : 'code';
  const previousAnotherValue = targetMaster.value[anotherType];
  targetMaster.value[anotherType] = t('読み込み中・・・');

  const resolverKey = `${selectedColumn.value}${type === 'code' ? 'Number' : 'Name'}`;
  const pair = await MasterPairResolverService.resolveMasterPairValue(resolverKey, value);
  if (requestId !== targetSelectionRequestId.value) return;

  if (pair?.value) {
    targetMaster.value[anotherType] = pair.value;
    resetManualPairSelection();
  } else {
    targetMaster.value[anotherType] = isManualPairSelectionRequired.value ? previousAnotherValue : '';
    if (!targetMaster.value[anotherType]) {
      isManualPairSelectionRequired.value = true;
      await showPairMissingModal();
    }
  }

  if (isManualPairSelectionRequired.value && (!targetMaster.value.code || !targetMaster.value.name)) {
    return;
  }
  await fetchBySelectedMasterPair(requestId);
};

/**
 * ターゲット情報に合わせてfetchする
 * @param requestId - 重複リクエストを防ぐためのID
 */
const fetchBySelectedMasterPair = async (requestId = targetSelectionRequestId.value) => {
  const filterItems: FilterContentItem[] = [];

  if (targetMaster.value.code) {
    filterItems.push({
      filterType: `${selectedColumn.value}Number`,
      selectedValue: [targetMaster.value.code],
      label: '',
    });
  }

  if (targetMaster.value.name) {
    filterItems.push({
      filterType: selectedColumn.value === 'warehouse' ? 'warehouse' : `${selectedColumn.value}Name`,
      selectedValue: [targetMaster.value.name],
      label: '',
    });
  }

  state.value.filterItems = filterItems;
  await fetchEditBusiness();
  if (requestId !== targetSelectionRequestId.value) return;

  editedMaster.value.code = targetMaster.value.code;
  editedMaster.value.name = targetMaster.value.name;
};

/**
 * 変更した値をセットする
 * @param type - タイプ
 * @param value - 値
 */
const setEditedMasterValue = (type: 'code' | 'name', value: string) => {
  editedMaster.value[type] = value;
};

/**
 * 編集した内容を確定する
 */
const commitChange = async () => {
  // バリデーション
  const result = await waitLoading(diContainer.updateMasterUseCase.execute(selectedColumn.value, targetMaster.value, editedMaster.value));
  switch (result?.status.type) {
    case 'success':
      toast.success(t('変更に成功しました。'));
      clearColumnSelection();
      return;
    case 'error':
    default:
      await showInfoModal({
        modalTitle: t('変更に失敗しました'),
        modalDescription:
          result?.status.error.code === 'RESOURCE_CONFLICT'
            ? t('変更後の選択肢が重複しています。')
            : t('エラーが発生しました。リロードして再度お試しください。'),
        colorStyle: 'error',
        icon: 'error',
        isDisabledNextButton: true,
        backButtonLabel: t('OK'),
      });
      return;
  }
};

onMounted(async () => {
  $reset();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(EditPageLayout, { title: pageTitle.value }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, _toDisplayString(_unref(t)('選択肢のコードや名称を変更します。変更内容はその項目を使用しているすべての取引に反映されます。')), 1),
          (isManualPairSelectionRequired.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_4, _toDisplayString(_unref(t)('対応する選択肢が取得できないため、コードと名称をそれぞれ選択してください。')), 1))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("div", _hoisted_5, [
          _createElementVNode("div", _hoisted_6, [
            _createVNode(SingleSelectInput, {
              label: _unref(t)('編集対象'),
              index: 0,
              placeholder: _unref(t)('選択してください'),
              "model-value": selectedColumn.value ? _unref(t)(`editMaster:${selectedColumn.value}`) : '',
              "filter-type": "MASTER_ITEMS",
              "prop-options": _unref(TargetMasterItems).map((m) => _unref(t)(`editMaster:${m}`)),
              "onUpdate:modelValue": setColumn
            }, null, 8, ["label", "placeholder", "model-value", "prop-options"]),
            (!!selectedColumn.value)
              ? (_openBlock(), _createBlock(SingleSelectInput, {
                  key: `target-code-${targetMasterFilterType.value.code}`,
                  label: _unref(t)('editMaster:コード'),
                  index: 1,
                  placeholder: _unref(t)('editMaster:コード'),
                  "model-value": targetMaster.value.code,
                  "filter-type": targetMasterFilterType.value.code,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = (e) => setTargetMasterValue('code', e))
                }, null, 8, ["label", "placeholder", "model-value", "filter-type"]))
              : _createCommentVNode("", true),
            (!!selectedColumn.value)
              ? (_openBlock(), _createBlock(SingleSelectInput, {
                  key: `target-name-${targetMasterFilterType.value.name}`,
                  label: _unref(t)('editMaster:名称'),
                  index: 2,
                  placeholder: _unref(t)('editMaster:名称'),
                  "model-value": targetMaster.value.name,
                  "filter-type": targetMasterFilterType.value.name,
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = (e) => setTargetMasterValue('name', e))
                }, null, 8, ["label", "placeholder", "model-value", "filter-type"]))
              : _createCommentVNode("", true),
            (canShowEditInputs.value)
              ? (_openBlock(), _createElementBlock("span", _hoisted_7, "→"))
              : _createCommentVNode("", true),
            (canShowEditInputs.value)
              ? (_openBlock(), _createBlock(TextInput, {
                  key: 3,
                  label: _unref(t)('変更後のコード'),
                  "model-value": editedMaster.value.code,
                  disabled: targetMaster.value.name !== editedMaster.value.name,
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = (e) => setEditedMasterValue('code', e))
                }, null, 8, ["label", "model-value", "disabled"]))
              : _createCommentVNode("", true),
            (canShowEditInputs.value)
              ? (_openBlock(), _createBlock(TextInput, {
                  key: 4,
                  label: _unref(t)('変更後の名称'),
                  "model-value": editedMaster.value.name,
                  disabled: targetMaster.value.code !== editedMaster.value.code,
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = (e) => setEditedMasterValue('name', e))
                }, null, 8, ["label", "model-value", "disabled"]))
              : _createCommentVNode("", true)
          ]),
          _createVNode(TextButton, {
            class: "header__button",
            disabled: isCommitDisable.value,
            onClick: commitChange
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref(t)('editMaster:更新する')), 1)
            ], undefined, true),
            _: 1
          }, 8, ["disabled"])
        ]),
        (targetMaster.value.name === '')
          ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
              _createElementVNode("div", _hoisted_9, _toDisplayString(_unref(t)('編集する選択肢を選択してください')), 1),
              _createElementVNode("div", _hoisted_10, _toDisplayString(_unref(t)('編集対象と現在のコード・名称を選択すると、該当する取引が一覧に表示されます。')), 1)
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_11, [
              _createVNode(EzTable, {
                ref_key: "tableRef",
                ref: tableRef,
                "grid-template-columns": headerWidths.value,
                "loading-status": _unref(state).loadingStatus,
                class: "list-table"
              }, {
                header: _withCtx(() => [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(headerOptions.value, (header) => {
                    return (_openBlock(), _createElementBlock(_Fragment, {
                      key: `header-${header.key}`
                    }, [
                      (header.key === 'headerModify')
                        ? (_openBlock(), _createBlock(EzHeader, {
                            key: 0,
                            sticky: "right",
                            class: "list-table__header"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(TableHeaderModifyButton, {
                                "modifiable-header-list": ModifiableHeaderKeys.value,
                                onToggle: _unref(updateDisplayHeader)
                              }, null, 8, ["modifiable-header-list", "onToggle"])
                            ], undefined, true),
                            _: 1
                          }))
                        : (_openBlock(), _createBlock(EzHeader, {
                            key: 1,
                            "use-resizer": "",
                            class: "list-table__header",
                            onResizer: (e) => _unref(onMousedownResizer)(e, header.key)
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(_unref(t)(header.label)), 1)
                            ], undefined, true),
                            _: 2
                          }, 1032, ["onResizer"]))
                    ], 64))
                  }), 128))
                ]),
                body: _withCtx(() => [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(state).dataList, (data, index1) => {
                    return (_openBlock(), _createBlock(EzTableRow, {
                      key: `row-${index1}`,
                      "grid-template-columns": headerWidths.value
                    }, {
                      default: _withCtx(() => [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(headerOptions.value, (header) => {
                          return (_openBlock(), _createElementBlock(_Fragment, {
                            key: `row-${index1}-${header.key}`
                          }, [
                            (header.key === 'headerModify')
                              ? (_openBlock(), _createBlock(EzCell, {
                                  key: 0,
                                  "line-height": 24,
                                  class: "list-table__cell"
                                }))
                              : (header.key.includes(selectedColumn.value))
                                ? (_openBlock(), _createBlock(EzCell, {
                                    key: 1,
                                    "line-height": 24,
                                    class: "list-table__cell list-table__cell--edited"
                                  }, {
                                    default: _withCtx(() => [
                                      _createTextVNode(_toDisplayString(_unref(vm).getDisplayData(header.key, data)) + " ", 1),
                                      (
                      editedMaster.value[header.key.includes('Number') ? 'code' : 'name'] !==
                      targetMaster.value[header.key.includes('Number') ? 'code' : 'name']
                    )
                                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                            _createTextVNode(" →" + _toDisplayString(header.key.includes('Number') ? editedMaster.value.code : editedMaster.value.name), 1)
                                          ], 64))
                                        : _createCommentVNode("", true)
                                    ], undefined, true),
                                    _: 2
                                  }, 1024))
                                : (_openBlock(), _createBlock(EzCell, {
                                    key: 2,
                                    "is-number": header.type === 'num',
                                    "line-height": 24,
                                    class: "list-table__cell"
                                  }, {
                                    default: _withCtx(() => [
                                      _createTextVNode(_toDisplayString(_unref(vm).getDisplayData(header.key, data)), 1)
                                    ], undefined, true),
                                    _: 2
                                  }, 1032, ["is-number"]))
                          ], 64))
                        }), 128))
                      ], undefined, true),
                      _: 2
                    }, 1032, ["grid-template-columns"]))
                  }), 128))
                ]),
                _: 1
              }, 8, ["grid-template-columns", "loading-status"])
            ]))
      ])
    ], undefined, true),
    _: 1
  }, 8, ["title"]))
}
}

})